import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { useTranslation } from "@coworker/locales";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import Linkbar from "../../../shared/components/Linkbar/Linkbar";
import ClickableButtonWrapper from "../../../shared/wrappers/ClickableButtonWrapper/ClickableButtonWrapper";
import FixaButton from "../../../shared/wrappers/FixaButton/FixaButton";
import FixaCarousel from "../../../shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "../../../shared/wrappers/FixaImage/FixaImage";
import {
  FixaOption,
  FixaSelect,
} from "../../../shared/wrappers/FixaSelect/FixaSelect";
import { FixaText } from "../../../shared/wrappers/FixaText/FixaText";
import { NotFound } from "../../../task-management/components/NotFound/NotFound";
import { mediaCards } from "../mock-media";
import styles from "./media-detail-page.module.css";

const mainMessageMainActivityValue = `Sleek on the outside. Sorted on the inside.

With clean lines and soft neutral colours, this kitchen reflects the essence of international modern style with balanced proportions, refined simplicity, and timeless appeal.

Behind the smooth surfaces, smart storage does the work. Pull-out drawers keep plates, pans, and everyday essentials in order, while upper cabinets and open shelving create both function and display space. No bending, no searching – just quiet, efficient organisation.

Elegant on the outside, practical on the inside – this kitchen proves that calm living comes from thoughtful design and knowing exactly where everything belongs.`;

const reasonForChange =
  "To emphasise the FY26 Extraordinary focus – Complete cooking and eating – in Launch 4.";

// Mirrors the media type taxonomy used across the Media Management POC.
const MEDIA_TYPE_OPTIONS = [
  "Home",
  "Room setting",
  "Vignette",
  "Product dominance wall",
  "All parts studio",
  "Selection point",
  "Display compact",
  "Cross over media",
  "Showcase",
  "Coordination media",
  "Vitality media",
  "Statement media",
  "Perimeter break",
  "Front media",
  "Open-the-wallet",
  "Opening offer",
];

export const MediaDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const media = useMemo(() => mediaCards.find((card) => card.id === id), [id]);

  const [mediaType, setMediaType] = useState(media?.mediaType ?? "");

  const linkbarItems = useMemo(
    () => [{ label: t("capitalCaseLaunchMediaString"), to: "/launch-media" }],
    [t]
  );

  if (!media) {
    return (
      <main className={styles["page"]}>
        <NotFound
          title={t("mediaNotFoundString")}
          message={t("noResultsString")}
        />
      </main>
    );
  }

  const images = media.images?.length ? media.images : [media.imageUrl];

  return (
    <main className={styles["page"]}>
      <Linkbar items={linkbarItems} currentLabel={media.title} currentAsH1 />

      <section className={styles["pageContent"]}>
        <div className={styles["carouselWrapper"]}>
          <FixaCarousel
            id="media-detail-carousel"
            className={styles["carousel"]}
            ariaLabelLeftBtn={t("previousImageString")}
            ariaLabelRightBtn={t("nextImageString")}
          >
            {images.map((src, index) => (
              <ClickableButtonWrapper
                key={[src, index].join("-")}
                aria-label={`Image ${index + 1} button`}
              >
                <FixaImage
                  className={styles["carouselImage"]}
                  src={src}
                  alt={
                    index === 0
                      ? media.imageAlt
                      : `${media.title} image ${index + 1}`
                  }
                />
              </ClickableButtonWrapper>
            ))}
          </FixaCarousel>

          <FixaButton
            className={styles["addImagesButton"]}
            htmlType="button"
            text={t("addImagesString")}
            type="secondary"
          />
        </div>

        <FixaText tagName="h2" className={styles["mediaTitle"]}>
          {t("workBriefString")}
        </FixaText>

        <div className={styles["field"]}>
          <FixaInputField
            id={"name"}
            type={"text"}
            value={media.title}
            readOnly
            label={t("nameString")}
          />
        </div>

        <div className={styles["field"]}>
          <FixaSelect
            id="mediaTypeSelect"
            label={t("selectMediaTypeString")}
            value={mediaType}
            onChange={(e) => setMediaType(e.target.value)}
          >
            {MEDIA_TYPE_OPTIONS.map((option) => (
              <FixaOption key={option} value={option} name={option} />
            ))}
          </FixaSelect>
        </div>

        <div className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("reasonForChangeString")}
          </FixaText>

          <FixaTextArea
            id={"reasonForChange"}
            value={reasonForChange}
            readOnly
            label={t("whyMediaNeedsToBeChangedString")}
          />
        </div>

        <div className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("mainMessageMainActivityString")}
          </FixaText>

          <FixaTextArea
            className={styles["mainMessageMainActivityComponent"]}
            id={"mainMessageMainActivity"}
            value={mainMessageMainActivityValue}
            readOnly
            label={t("specificNeedToBeSolvedString")}
          />
        </div>
      </section>
    </main>
  );
};
